/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellEditingStartedEvent,
  CellPosition,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  SuppressKeyboardEventParams,
  TabToNextCellParams,
  ValueFormatterParams,
  ValueParserParams,
} from '@ag-grid-community/core';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { EventManager } from '@angular/platform-browser';
import { ChartConfiguration } from 'chart.js';
import dayjs from 'dayjs';
import { first, isEqual, last, omit, pick } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { BlendedCurveModalDataModel } from '@features/blended-curve-modal/blended-curve-modal-data.model';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineService } from '@models/milestone-category/milestone/timeline.service';
import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { PatientCurveService } from '@models/patient-curve/patient-curve.service';
import { PatientCurveModel, PatientCurveStore } from '@models/patient-curve/patient-curve.store';
import { PatientGroupsQuery } from '@models/patient-group/patient-groups.query';
import { PatientGroupsService } from '@models/patient-group/patient-groups.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CanvasChartComponent } from '@shared/components/canvas-chart/canvas-chart.component';
import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { EditableListDropdownComponent } from '@shared/components/editable-list-dropdown/editable-list-dropdown.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  RemoveDialogComponent,
  RemoveDialogInput,
} from '@shared/components/remove-dialog/remove-dialog.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { ApiService } from '@shared/services/api.service';
import {
  CurveType,
  DistributionMode,
  EntityType,
  EventType,
  listDriverPatientDistributionsQuery,
  Organization,
  PermissionType,
  UpdateDriverPatientDistributionInput,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { TableService } from '@shared/services/table.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import {
  batchPromises,
  DateUtils,
  decimalAdd,
  decimalDifference,
  decimalEquality,
  decimalRoundingToNumber,
  decimalRoundingToString,
} from '@shared/utils';
import { AgGridGetData } from '@shared/utils/ag-grid-data.utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { PatientsQuery } from './models/patients.query';
import { PatientsService } from './models/patients.service';
import { CurveWorkflowDirective } from '../../directives/curve-workflow.directive';
import { DistributionTimelineService } from '../../services/distribution-timeline.service';
import { AgPatientDriverComparisonPanelComponent } from './components/ag-patient-driver-comparison-panel/ag-patient-driver-comparison-panel.component';
import { AgPatientDriverUpperCardsComponent } from './components/ag-patient-driver-upper-cards-component/ag-patient-driver-upper-cards-component';
import { PatientBlendedCurveModalComponent } from './components/patient-blended-curve-modal/patient-blended-curve-modal.component';
import { PatientDriverUploadComponent } from './components/patient-driver-upload/patient-driver-upload.component';
import {
  ComparisonCard,
  ForecastAndActualType,
  PatientCurvesConstants,
  TopCard,
} from './constants/patient-curves.constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [CurveWorkflowDirective],
  imports: [
    MenuComponent,
    IconComponent,
    EditableListDropdownComponent,
    InputComponent,
    VarDirective,
    NgClass,
    AgPatientDriverUpperCardsComponent,
    CanvasChartComponent,
    AgPatientDriverComparisonPanelComponent,
    ButtonComponent,
    TooltipDirective,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
  ],
  selector: 'aux-patient-curves',
  styleUrls: ['patient-curves.component.scss'],
  templateUrl: './patient-curves.component.html',
})
export class PatientCurvesComponent implements OnInit {
  organizationQuery = inject(OrganizationQuery);
  patientCurveQuery = inject(PatientCurveQuery);
  patientGroupsQuery = inject(PatientGroupsQuery);
  workflowCtx = inject(CurveWorkflowDirective);

  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private distributionTimelineService = inject(DistributionTimelineService);
  private eventManager = inject(EventManager);
  private eventService = inject(EventService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private patientCurveService = inject(PatientCurveService);
  private patientCurveStore = inject(PatientCurveStore);
  private patientGroupsService = inject(PatientGroupsService);
  private patientQuery = inject(PatientsQuery);
  private patientService = inject(PatientsService);
  private timelineQuery = inject(TimelineQuery);
  private timelineService = inject(TimelineService);
  private vendorsService = inject(OrganizationService);

  isEqual = signal<boolean>(false);
  wasGridChanged = signal<boolean>(false);

  userHasModifyPermissions = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_PATIENT_CURVE],
  });

  disableModifyPatientCurve = computed(() => {
    return !this.userHasModifyPermissions() || this.workflowCtx.isLocked();
  });
  disableModifyPatientCurveTooltip = computed(() => {
    if (!this.userHasModifyPermissions()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return this.workflowCtx.tooltip();
  });
  saveBtnTitle = computed(() => {
    return !this.wasGridChanged()
      ? 'You need to make one or more changes to save'
      : !this.isEqual()
        ? 'This patient curve is unable to be saved until the following issue is resolved: Patients Enrolled must be equal to the sum of Patients Complete and Patients Discontinued.'
        : '';
  });

  btnLoading$ = new BehaviorSubject(false);
  clonedPatient: listDriverPatientDistributionsQuery[] = [];
  comparisonCards$ = new BehaviorSubject<ComparisonCard[]>([]);
  currentPatientGroupId = '';
  currentPatientGroupName$ = new BehaviorSubject<string>('');
  curveControl = new FormControl('');
  curveEditedVal = '';
  driverSettingId$ = new BehaviorSubject<null | string>(null);
  editCell = false;
  editingGridCell$ = new BehaviorSubject(false);
  editMode$ = new BehaviorSubject(false);
  gridApi!: GridApi;
  gridData$ = new BehaviorSubject<listDriverPatientDistributionsQuery[]>([]);
  timelineList$ = new BehaviorSubject<{ endDate?: string; startDate?: string }>({
    endDate: '',
    startDate: '',
  });
  gridOptions = {
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellClass: PatientCurvesConstants.getCellClasses(this.editMode$),
      cellClassRules: {
        '!bg-aux-error': (params) => {
          let ret = false;

          switch (params.colDef.field) {
            case 'forecast.net_patients_enrolled':
              if (
                !this.isBlended$.getValue() &&
                Utils.isNegativeAndNotCloseEnoughToZero(params.data.forecast.net_patients_enrolled)
              ) {
                ret = true;
                this.isNetPatientNegative$.next(true);
              }

              break;
            case 'forecast.patients_complete':
            case 'forecast.patients_discontinued':
            case 'forecast.patients_enrolled':
              if (params.data.distribution_month === 'TOTAL' && !this.isBlended$.getValue()) {
                const diffEnrolledToDiscontinued = decimalDifference(
                  params.data.forecast.patients_enrolled,
                  params.data.forecast.patients_discontinued
                );

                ret = !decimalEquality(
                  diffEnrolledToDiscontinued,
                  params.data.forecast.patients_complete
                );
                this.isEqual.set(!ret);
              }

              break;
            default:
              break;
          }

          return ret;
        },
      },
      cellStyle: (params) => {
        if (
          (params.colDef.field === 'forecast.cumulative_enrollment_percentage' ||
            params.colDef.field?.includes('actual') ||
            params.colDef.field?.includes('net_patients_enrolled') ||
            params.colDef.field?.includes('total_patients_enrolled')) &&
          params.data.distribution_month !== 'TOTAL'
        ) {
          return this.editCell
            ? { 'justify-item': 'end', opacity: 0.7 }
            : { 'justify-item': 'end' };
        }

        return {};
      },
      editable: () => {
        return this.editMode$.getValue();
      },
      headerClass: PatientCurvesConstants.getHeaderClasses(this.editMode$),
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        if (!params.editing) {
          switch (params.event.key) {
            case 'Backspace':
            case 'Delete':
              if (this.editMode$.getValue()) {
                TableService.clearCellRange(params, () => this.calculatePatientCurves());
                this.onCellValueChanged();
              }

              return true;
            default:
              return false;
          }
        }

        return false;
      },
    },
    ...PatientCurvesConstants.GRID_OPTIONS,
    ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
    columnDefs: PatientCurvesConstants.allColDefs({
      currentPatientGroupName$: this.currentPatientGroupName$,
      editCell: this.editCell,
      showEDCColumns: this.launchDarklyService.flags$.getValue().patient_drivers_edc,
      timeline$: this.timelineList$,
      valueFormatter: this.valueFormatter,
      valueParser: this.numberParser,
    }),
    excelStyles: PatientCurvesConstants.GRID_OPTIONS_EXEL_STYLES,
    processCellFromClipboard: (params) => {
      return Utils.castStringToNumber(params.value).toString();
    },
  } as GridOptions;
  isBlended$ = new BehaviorSubject(false);
  isFromScratch = false;
  isInitialValueAlreadySet = false;
  isNetPatientNegative$ = new BehaviorSubject(false);
  loading$ = new BehaviorSubject(false);
  readonly messagesConstants = MessagesConstants;
  monthList: { end_date: Date | string; start_date: string } = { end_date: '', start_date: '' };
  multiChart$: Observable<ChartConfiguration<'line'>> = this.gridData$.pipe(
    map((data) => {
      const labels = data.map(({ distribution_month }) =>
        Utils.dateFormatter(distribution_month, { day: undefined, year: '2-digit' })
      );

      return PatientCurvesConstants.multiChartOptions(data, labels);
    })
  );
  PatientCurveGroup: PatientCurveModel[] = [];
  patientCurveGroups: PatientCurveModel[] = [];
  patientCurveListOptions: EditableListDropdownItem[] = [];
  patientDriversEdcFlag = this.launchDarklyService.$select((flags) => flags.patient_drivers_edc);
  selectedMonth$ = new BehaviorSubject<string>('');
  selectedVendor = new UntypedFormControl('');
  showEnrollmentSettings = this.launchDarklyService.$select(
    (flags) => flags.section_patient_driver_enrollment_settings
  );
  topCards$ = new BehaviorSubject<TopCard[]>([]);
  userHasUploadPatientDriverPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPLOAD_PATIENT_DRIVER],
  });
  zeroValues = {
    cumulative_enrollment_percentage: 0,
    net_patients_enrolled: 0,
    patients_complete: 0,
    patients_discontinued: 0,
    patients_enrolled: 0,
    total_patients_enrolled: 0,
  };

  // eslint-disable-next-line max-lines-per-function
  ngOnInit() {
    this.timelineService.getTimelineItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.vendorsService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const vendors = this.organizationQuery.getAllVendors();

        if (vendors.length === 1) {
          this.organizationStore.setActive(vendors[0].id);
          this.selectedVendor.setValue(vendors[0].id);
        } else {
          // reset any older selected vendors.
          this.organizationStore.setActive(null);
          this.selectedVendor.setValue('');
        }
      });

    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.loading$.next(true);

          return combineLatest([this.patientCurveService.get(), this.patientGroupsService.get()]);
        }),
        tap(() => {
          this.isInitialValueAlreadySet = false;
          this.monthList.start_date = '';
          this.clonedPatient = Utils.clone([]);
          this.gridData$.next(this.clonedPatient);
        }),
        switchMap(() => {
          return combineLatest([
            this.patientCurveQuery.selectAll(),
            this.patientGroupsQuery.selectAll(),
          ]).pipe(
            map(([curves, patientGroups]) => {
              this.PatientCurveGroup = curves;
              const pgIds: string[] = [];

              curves.forEach((c) => {
                pgIds.push(c.patient_group_id);
              });
              const uniqPGs: PatientCurveModel[] = [];

              patientGroups.forEach((pg) => {
                if (!pgIds.includes(pg.id)) {
                  uniqPGs.push({
                    __typename: 'DriverPatientGroup',
                    constituent_patient_groups: [],
                    curve_type: CurveType.NET,
                    driver_setting_id: '',
                    is_blended: false,
                    name: pg.name,
                    organizations_with_forecasts: [],
                    patient_group_id: pg.id,
                    patient_group_ids: [],
                    showLine: false,
                  });
                }
              });
              const patientItems = [...curves, ...uniqPGs];

              this.updatePatientCurveList(patientItems);

              if (patientItems.length === 0) {
                this.curveControl.setValue('');
              } else if (
                this.curveControl.value !== patientItems[0].name &&
                !this.isInitialValueAlreadySet
              ) {
                this.curveControl.setValue(patientItems[0].name);
              } else if (this.curveEditedVal !== '') {
                this.curveControl.setValue(this.curveEditedVal);
                this.curveEditedVal = '';
              }

              return patientItems;
            })
          );
        })
      )
      .subscribe(() => {
        this.loading$.next(false);
        this.isInitialValueAlreadySet = true;
      });

    this.eventManager.addEventListener(document.body, 'keydown', (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Tab':
          this.gridApi.stopEditing();
          setTimeout(() => {
            const getCellCor = this.gridApi.getFocusedCell();

            this.gridApi.startEditingCell({
              colKey: getCellCor?.column?.getColId() || '',
              rowIndex: getCellCor?.rowIndex || 0,
            });
          }, 0);
          break;
        default:
          break;
      }
    });

    //temporary fix, should be investigated later
    combineLatest([this.curveControl.valueChanges, this.mainQuery.select('trialKey')])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([value]) => {
        const driverPatientGroup =
          this.patientCurveGroups?.find((item) => item.name === value) || null;

        if (driverPatientGroup) {
          this.selectCurve(driverPatientGroup);
        }
      });
  }

  private blendedCurveModalParams(
    item?: null | PatientCurveModel
  ): BlendedCurveModalDataModel<PatientCurveModel> {
    return {
      availableGroups: this.PatientCurveGroup.filter(
        (group) => !group.is_blended && group.patient_group_id
      ).filter(
        (value, index, self) =>
          index === self.findIndex((group) => group.driver_setting_id === value.driver_setting_id)
      ),
      blendedCurve: item || null,
      existingCurvesNames: this.patientCurveGroups
        .map((group) => group.name)
        .filter((name) => name !== item?.name),
      text: {
        subTitle: 'Select Patient Groups',
        title: 'Blended Patient Curve',
      },
    };
  }

  calculatePatientCurves(): void {
    let totalNetPatientEnrolled = 0;
    let sumOfNetPatientEnrolled = 0;
    let patientEnrolledTotal = 0;
    let isNetPatientNegative = false;

    this.gridData$.getValue().forEach((x: listDriverPatientDistributionsQuery) => {
      x.forecast.patients_enrolled = decimalRoundingToNumber(x.forecast.patients_enrolled || 0, 8);
      x.forecast.patients_discontinued = decimalRoundingToNumber(
        x.forecast.patients_discontinued || 0,
        8
      );
      x.forecast.patients_complete = decimalRoundingToNumber(x.forecast.patients_complete || 0, 8);

      const enrolledLessDiscontinued = decimalDifference(
        x.forecast.patients_enrolled,
        x.forecast.patients_discontinued
      );

      const lessPatientsComplete = decimalDifference(
        enrolledLessDiscontinued,
        x.forecast.patients_complete
      );

      totalNetPatientEnrolled = decimalAdd(totalNetPatientEnrolled, lessPatientsComplete);
      sumOfNetPatientEnrolled = decimalAdd(sumOfNetPatientEnrolled, totalNetPatientEnrolled);

      x.forecast.net_patients_enrolled = decimalRoundingToNumber(totalNetPatientEnrolled, 8);
      patientEnrolledTotal = decimalAdd(patientEnrolledTotal, x.forecast.patients_enrolled);
      x.forecast.total_patients_enrolled = decimalRoundingToNumber(patientEnrolledTotal, 8);

      isNetPatientNegative = Utils.isNegativeAndNotCloseEnoughToZero(
        x.forecast.net_patients_enrolled
      );
    });
    this.isNetPatientNegative$.next(isNetPatientNegative);

    this.gridData$.getValue().forEach((x: listDriverPatientDistributionsQuery) => {
      x.forecast.cumulative_enrollment_percentage = Utils.calculateAsPercentage(
        sumOfNetPatientEnrolled,
        x.forecast.net_patients_enrolled || 0
      );
    });

    this.gridData$.next(this.gridData$.getValue());
    this.gridApi.refreshCells();
  }

  calculatePinnedBottomData(fillComparisonCards = false) {
    const currentMonth = this.selectedMonth$.getValue();
    const gridData =
      fillComparisonCards && currentMonth
        ? this.gridData$
            .getValue()
            .filter((row) => !dayjs(row.distribution_month).isAfter(currentMonth))
        : this.gridData$.getValue();

    const forecast = {
      cumulative_enrollment_percentage: 0,
      net_patients_enrolled: 0,
      patients_complete: 0,
      patients_discontinued: 0,
      patients_enrolled: 0,
      total_patients_enrolled: 0,
    };
    const actual = {
      cumulative_enrollment_percentage: 0,
      net_patients_enrolled: 0,
      patients_complete: 0,
      patients_discontinued: 0,
      patients_enrolled: 0,
      total_patients_enrolled: 0,
    };

    const columnsWithAggregation = Object.keys(actual).filter((key) => {
      return key !== 'net_patients_enrolled';
    }) as unknown as (keyof ForecastAndActualType)[];

    gridData.forEach((val) => {
      for (const key of columnsWithAggregation) {
        const currentValForForecast = forecast[key];
        const additionalValForForecast = val.forecast[key];

        const currentValForActual = actual[key];
        const additionalValForActual = val.actual ? val.actual[key] : 0;

        if (Utils.isNumber(currentValForForecast) && Utils.isNumber(additionalValForForecast)) {
          if ('total_patients_enrolled' === key) {
            forecast[key] = additionalValForForecast ? additionalValForForecast : 0; //always set total patients enrolled to last months value, not net
          } else {
            forecast[key] = decimalAdd(
              currentValForForecast,
              additionalValForForecast ? additionalValForForecast : 0
            );
          }
        }

        if (Utils.isNumber(currentValForActual) && Utils.isNumber(additionalValForActual)) {
          if ('total_patients_enrolled' === key) {
            actual[key] = additionalValForActual ? additionalValForActual : 0; //always set total patients enrolled to last months value, not net
          } else {
            actual[key] = decimalAdd(
              currentValForActual,
              additionalValForActual ? additionalValForActual : 0
            );
          }
        }
      }

      forecast.total_patients_enrolled = 0;
      actual.total_patients_enrolled = 0;
    });

    return { actual, forecast };
  }

  cancelEditMode() {
    if (this.isFromScratch) {
      this.populateGridDataWithTimeline();
    } else {
      this.clonePatient();
    }

    this.gridApi.stopEditing(true);
    this.editMode$.next(false);
    this.isNetPatientNegative$.next(false);
    this.editCell = false;
    this.gridApi.refreshCells({ force: true });
  }

  async canDeactivate(): Promise<boolean> {
    const hasChanges = this.didGridHasChanges();

    if (hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellEditingStopped() {
    this.editingGridCell$.next(false);
  }

  cellValueChanged() {
    this.calculatePatientCurves();
  }

  checkGridHasChanges(): void {
    this.wasGridChanged.set(this.didGridHasChanges());
  }

  clonePatient() {
    const pData = this.patientQuery.getAll();

    this.patientCurveService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.setGridData(pData);
  }

  constructor() {
    this.timelineQuery
      .selectStartEndDateTrial()
      .pipe(takeUntilDestroyed())
      .subscribe((dates) => {
        this.timelineList$.next(dates);
      });

    this.editMode$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.gridApi) {
        this.gridApi.refreshHeader();
      }
    });

    void this.getSelectedMonth();
  }

  // eslint-disable-next-line max-lines-per-function
  async deleteCurve(patientCurveGroup: EditableListDropdownItem) {
    const driverPatientGroup = this.patientCurveGroups.find(
      (item) => item.name === patientCurveGroup.name
    );

    if (!driverPatientGroup) {
      return;
    }

    const cannotDeleteMessage = `${driverPatientGroup.name} cannot be deleted because it is being used in other areas of the application.<br>Please first remove the dependencies below and then ${driverPatientGroup.name} can be successfully deleted.<br>`;

    let blendedCurves: PatientCurveModel[] = [];

    if (!driverPatientGroup.is_blended) {
      const patientCurves = this.patientCurveQuery.getAll();

      blendedCurves = patientCurves.filter((el) =>
        el.constituent_patient_groups.find((c) => c.id === driverPatientGroup.patient_group_id)
      );
    }

    if (
      driverPatientGroup.is_blended &&
      driverPatientGroup.organizations_with_forecasts.length > 0
    ) {
      const routeInputs = [
        {
          componentName: 'Forecast Methodology:',
          link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
          name: driverPatientGroup.organizations_with_forecasts.map(
            ({ name }: Organization) => name
          ),
        },
      ];

      const removeDialogInput: RemoveDialogInput = {
        cannotDeleteMessage,
        routeInputs,
      };

      const resp = this.overlayService.openPopup({
        content: RemoveDialogComponent,
        data: removeDialogInput,
        settings: {
          header: 'Remove Patient Curve',
          primaryButton: {
            disabled: true,
            label: 'Remove',
          },
        },
      });

      await firstValueFrom(resp.afterClosed$);
    } else if (
      !driverPatientGroup.is_blended &&
      (driverPatientGroup.organizations_with_forecasts.length > 0 || blendedCurves.length > 0)
    ) {
      const routeInputs = [
        {
          componentName: 'Blended Curves:',
          link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}`,
          name: blendedCurves.map(({ name }) => name),
        },
      ];

      if (driverPatientGroup.organizations_with_forecasts.length) {
        const routeInputsPatientGroup = [
          {
            componentName: 'Forecast Methodology:',
            link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
            name: driverPatientGroup.organizations_with_forecasts.map(({ name }) => name),
          },
        ];

        routeInputs.push(...routeInputsPatientGroup);
      }

      const removeDialogInput: RemoveDialogInput = {
        cannotDeleteMessage,
        routeInputs,
      };

      const resp = this.overlayService.openPopup({
        content: RemoveDialogComponent,
        data: removeDialogInput,
        settings: {
          header: 'Remove Patient Curve',
          primaryButton: {
            disabled: true,
            label: 'Remove',
          },
        },
      });

      await firstValueFrom(resp.afterClosed$);
    } else {
      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure you want to remove Patient Curve ${driverPatientGroup.name}?`,
        },
        settings: {
          header: 'Remove Patient Curve',
          primaryButton: {
            action: async (instance) => {
              instance?.ref.close();
              this.curveControl.setValue('');
              await this.patientCurveService.remove(driverPatientGroup);
              await firstValueFrom(this.patientCurveService.get().pipe(take(1)));
              const patientCurveServiceData = this.patientCurveQuery.getAll();

              if (patientCurveServiceData) {
                this.PatientCurveGroup = patientCurveServiceData;
              }

              this.gridData$.next([]);
              this.gridApi.refreshCells({ force: true });
              this.populateGridDataWithTimeline(true);
            },
            label: 'Remove',
          },
        },
      });
    }
  }

  didGridHasChanges(): boolean {
    const fieldsToCompare = [
      'forecast.patients_enrolled',
      'forecast.patients_discontinued',
      'forecast.patients_complete',
    ];
    const currentValuesToCompare = AgGridGetData(<GridApi>this.gridApi).map((curve) =>
      pick(curve, fieldsToCompare)
    );
    const initialValuesToCompare = this.clonedPatient.map((curve) => pick(curve, fieldsToCompare));

    const castToNumberArray = (colData: Partial<listDriverPatientDistributionsQuery>[]) => {
      const castedData = colData;

      castedData.forEach((val) => {
        if (val.forecast) {
          val.forecast.patients_enrolled = !val.forecast?.patients_enrolled
            ? 0
            : Number(val.forecast.patients_enrolled);
          val.forecast.patients_discontinued = !val.forecast?.patients_discontinued
            ? 0
            : Number(val.forecast.patients_discontinued);
          val.forecast.patients_complete = !val.forecast?.patients_complete
            ? 0
            : Number(val.forecast.patients_complete);
        }
      });

      return castedData;
    };

    const castedCurrent = castToNumberArray(currentValuesToCompare);
    const castedInitial = castToNumberArray(initialValuesToCompare);

    return !isEqual(castedCurrent, castedInitial) && this.editMode$.getValue();
  }

  editCurve(patientCurveGroup: EditableListDropdownItem | null = null) {
    const driverPatientGroup: null | PatientCurveModel = patientCurveGroup
      ? this.patientCurveGroups?.find((item) => item.name === patientCurveGroup.name) || null
      : null;

    const modalRef = this.overlayService.openPopup<
      unknown,
      {
        data: { name: string };
      }
    >({
      data: this.blendedCurveModalParams(driverPatientGroup),
      modal: PatientBlendedCurveModalComponent,
      settings: {
        header: `${driverPatientGroup ? 'Edit' : 'Create'} Blended Patient Curve`,
        primaryButton: {
          label: `${driverPatientGroup ? 'Update' : 'Create'} Curve`,
        },
      },
    });

    modalRef.afterClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ data }) => {
      // cancel modal will be null
      if (data?.data?.name) {
        this.curveEditedVal = data.data.name; //for edit reselection, name edit
        this.curveControl.setValue(data.data.name); //for add selection
      }
    });
  }

  editGrid() {
    this.editCell = true;
    this.gridApi.refreshCells({ force: true });

    this.gridApi.startEditingCell({
      colKey: 'forecast.patients_enrolled',
      rowIndex: 0,
    });
  }

  fillAllCards() {
    const totalsToDate = this.calculatePinnedBottomData(true);
    const targetTotals = this.calculatePinnedBottomData();

    this.fillComparisonCards(totalsToDate, targetTotals.forecast);
    this.fillForecastCards(targetTotals.forecast);
  }

  // eslint-disable-next-line max-lines-per-function
  fillComparisonCards(
    totalsToDate: { actual: ForecastAndActualType; forecast: ForecastAndActualType },
    targetForecast: ForecastAndActualType
  ) {
    const options = {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };

    this.comparisonCards$.next([
      {
        compareFrom: {
          percent: this.percentageFormatterForCards(
            'Enrolled',
            targetForecast.patients_enrolled || 0,
            totalsToDate.forecast.patients_enrolled || 0
          ),
          title: 'Forecast',
          value: Utils.decimalFormatter(totalsToDate.forecast.patients_enrolled || 0, options),
        },
        compareTo: {
          percent: this.percentageFormatterForCards(
            'Enrolled',
            targetForecast.patients_enrolled || 0,
            totalsToDate.actual.patients_enrolled || 0
          ),
          title: 'EDC Actuals',
          value: Utils.decimalFormatter(totalsToDate.actual.patients_enrolled || 0, options),
        },
        title: 'Total Patients Enrolled',
      },
      {
        compareFrom: {
          percent: this.percentageFormatterForCards(
            'Discontinued',
            targetForecast.patients_enrolled || 0,
            totalsToDate.forecast.patients_discontinued || 0
          ),
          title: 'Forecast',
          value: Utils.decimalFormatter(totalsToDate.forecast.patients_discontinued || 0, options),
        },
        compareTo: {
          percent: this.percentageFormatterForCards(
            'Discontinued',
            targetForecast.patients_enrolled || 0,
            totalsToDate.actual.patients_discontinued || 0
          ),
          title: 'EDC Actuals',
          value: Utils.decimalFormatter(totalsToDate.actual.patients_discontinued || 0, options),
        },
        title: 'Total Patients Discontinued',
      },
      {
        compareFrom: {
          percent: this.percentageFormatterForCards(
            'Complete',
            targetForecast.patients_enrolled || 0,
            totalsToDate.forecast.patients_complete || 0
          ),
          title: 'Forecast',
          value: Utils.decimalFormatter(totalsToDate.forecast.patients_complete || 0, options),
        },
        compareTo: {
          percent: this.percentageFormatterForCards(
            'Complete',
            targetForecast.patients_enrolled || 0,
            totalsToDate.actual.patients_complete || 0
          ),
          title: 'EDC Actuals',
          value: Utils.decimalFormatter(totalsToDate.actual.patients_complete || 0, options),
        },
        title: 'Total Patients Completed',
      },
    ]);
  }

  fillForecastCards(forecast: ForecastAndActualType) {
    const options = {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };

    this.topCards$.next([
      {
        title: 'Total Target Enrollment',
        value: Utils.decimalFormatter(forecast.patients_enrolled || 0, options),
      },
      {
        title: 'Total Forecast Patients Discontinued',
        value: Utils.decimalFormatter(forecast.patients_discontinued || 0, options),
      },
      {
        title: 'Total Forecast Patients Completed',
        value: Utils.decimalFormatter(forecast.patients_complete || 0, options),
      },
    ]);
  }

  getFilePath(id: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/sites/${id}/patient-driver/`;
  }

  getSelectedMonth() {
    const currentOpenMonth = this.mainQuery.getValue().currentOpenMonth;

    if (currentOpenMonth) {
      this.selectedMonth$.next(dayjs(currentOpenMonth).endOf('month').format('YYYY-MM-DD'));
    } else {
      this.selectedMonth$.next(dayjs().endOf('month').format('YYYY-MM-DD'));
    }
  }

  numberParser(params: ValueParserParams): number | string {
    const num =
      params.newValue === params.oldValue
        ? params.oldValue
        : Utils.castStringToNumber(params.newValue).toString();

    return (Number.isNaN(num) ? params.oldValue : Math.abs(num)).toString();
  }

  onCellValueChanged(): void {
    this.checkGridHasChanges();
  }

  onDataRendered({ api }: { api: GridApi }) {
    api.sizeColumnsToFit();
  }

  onEditClick(): void {
    this.editMode$.next(true);
    this.checkGridHasChanges();
    this.editGrid();
  }

  async onExportForPatientDriver() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next(true);

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.PATIENT_CURVE,
          filename: `${trialName}_patient_curves`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  onGridReady(e: GridReadyEvent) {
    this.gridApi = e.api;
    this.gridData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.editMode$.getValue()) {
        this.fillAllCards();
      }

      const pinnedBottomData = { ...this.calculatePinnedBottomData(), distribution_month: 'TOTAL' };

      this.gridApi.setGridOption('pinnedBottomRowData', [pinnedBottomData]);
    });
  }

  onPatientDriverUploadClick() {
    type InstanceType = null | PatientDriverUploadComponent;
    this.overlayService.openPopup({
      content: PatientDriverUploadComponent,
      settings: {
        header: 'Upload Patient Driver',
        primaryButton: {
          action: (instance: InstanceType) => instance?.onUpload(),
          label: 'Upload',
        },
      },
    });
  }

  async onSaveAll() {
    this.btnLoading$.next(true);
    const isInvalid = this.showErrors();

    if (isInvalid) {
      this.btnLoading$.next(false);
      this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

      return;
    }

    if (!isInvalid) {
      if (this.isFromScratch) {
        await this.processPatientsCreatedFromScratch();
        this.fillAllCards();
      } else {
        this.patientCurveStore.setLoading(true);

        const gData: UpdateDriverPatientDistributionInput[] = this.gridData$.getValue().map((x) => {
          return {
            cumulative_enrollment_percentage: x.forecast.cumulative_enrollment_percentage,
            distribution_mode: x.forecast.distribution_mode,
            distribution_month: x.distribution_month,
            id: x.forecast.id || '',
            net_patients_enrolled: x.forecast.net_patients_enrolled,
            patients_complete: x.forecast.patients_complete,
            patients_discontinued: x.forecast.patients_discontinued,
            patients_enrolled: x.forecast.patients_enrolled,
            total_patients_enrolled: x.forecast.total_patients_enrolled,
          };
        });

        const responses = await batchPromises(gData, (p) =>
          this.patientService.updatePatientDistribution(p)
        );

        if (!responses.every((x) => x)) {
          this.btnLoading$.next(false);
          this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

          return;
        }

        this.overlayService.success('Patients Table updated successfully!');

        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            entity_id:
              this.currentPatientGroupId === null || this.currentPatientGroupId === ''
                ? ''
                : this.currentPatientGroupId.toString(),
            entity_type: EntityType.DRIVER,
            type: EventType.PATIENT_DRIVER_DISTRIBUTION_UPDATED,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          this.overlayService.error(errors);
        }

        this.cancelEditMode();
        this.fillAllCards();
      }
    }

    this.editMode$.next(false);
    this.btnLoading$.next(false);
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  percentageFormatterForCards(type: string, num1: number, num2: number): string {
    const options = {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };
    const formattedValue = Utils.percentageFormatter(
      Utils.calculateAsPercentage(num1, num2) / 100,
      options
    );

    return formattedValue === Utils.zeroHyphen ? `0% ${type}` : `${formattedValue} ${type}`;
  }

  populateGridDataWithTimeline(afterRemove = false) {
    const patientQueryData = this.patientQuery.getAll();

    if (afterRemove) {
      this.timelineService.getTimelineItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.timelineQuery
        .select()
        .pipe(take(2))
        .subscribe(({ items }) => {
          const start_date = first(items)?.contract_start_date;
          let end_date = Utils.dateParse(`${last(items)?.contract_end_date.slice(0, 8)}01`);

          items.forEach((i) => {
            const parsedDate = Utils.dateParse(`${i.contract_end_date.slice(0, 8)}01`);

            if (parsedDate > end_date) {
              end_date = parsedDate;
            }
          });

          if (start_date) {
            this.monthList.start_date = start_date;
            this.monthList.end_date = end_date || '';

            this.setEmptyGridData();
          }
        });
    } else {
      this.setGridData(patientQueryData);
    }

    this.isFromScratch = true;
  }

  async processPatientsCreatedFromScratch() {
    const thingsToUpdate: listDriverPatientDistributionsQuery[] = [];

    this.gridApi.forEachNode((node) => {
      thingsToUpdate.push(node.data);
    });
    const data = thingsToUpdate.map((row) => ({
      cumulative_enrollment_percentage: row.forecast.cumulative_enrollment_percentage,

      month: Utils.dateFormatter(row.distribution_month, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      }),
      net_patients_enrolled: row.forecast.net_patients_enrolled,
      patients_complete: row.forecast.patients_complete,
      patients_discontinued: row.forecast.patients_discontinued,
      patients_enrolled: row.forecast.patients_enrolled,
      total_patients_enrolled: row.forecast.total_patients_enrolled,
    }));
    const key = `${this.getFilePath(
      this.curveControl.value !== '' ? this.currentPatientGroupId.toString() : 'Patient-Curve'
    )}patient-curve-from-scratch.csv`;
    const blob = new Blob([Utils.objectToCsv(data)], { type: 'text/csv' });
    const fileSuccess = await this.apiService.uploadFile(key, new File([blob], key));

    if (fileSuccess) {
      const { errors, success } = await firstValueFrom(
        this.eventService.processEvent$({
          bucket_key: `public/${key}`,
          entity_id: this.curveControl.value !== '' ? this.currentPatientGroupId.toString() : '',
          entity_type: EntityType.DRIVER,
          payload: JSON.stringify({
            should_show_on_document_library: false,
          }),
          type: EventType.PATIENT_DRIVER_TEMPLATE_UPLOADED,
        })
      );

      if (success) {
        this.loading$.next(true);
        // allows time for PATIENT_DRIVER_TEMPLATE_UPLOADED event to complete
        await new Promise((resolve) => setTimeout(resolve, 5000));
        this.overlayService.success();
        this.isFromScratch = false;
        this.resetPatientCurveGroup();
        await firstValueFrom(this.patientGroupsService.get().pipe(take(1)));
        await firstValueFrom(
          this.patientService
            .get(this.curveControl.value === '' ? undefined : this.currentPatientGroupId.toString())
            .pipe(take(1))
        );

        if (this.curveControl.value === '') {
          this.curveControl.setValue('Patient Curve');
        }

        this.clonePatient();
        this.gridApi.stopEditing();
        this.editMode$.next(false);
        this.editCell = false;
        this.gridApi.refreshCells({ force: true });
        this.loading$.next(false);
      } else {
        this.overlayService.error(errors);
      }
    } else {
      this.overlayService.error(MessagesConstants.FILE.ERROR_UPLOADING_FILE);
    }
  }

  resetPatientCurveGroup() {
    this.PatientCurveGroup = [];
  }

  rowPinnedCheck(event: CellEditingStartedEvent) {
    if (event.node.rowPinned) {
      this.gridApi.stopEditing();
    } else {
      this.editingGridCell$.next(true);
    }
  }

  async saveEditMode() {
    const isEqual = this.isEqual();

    if (!isEqual) {
      this.overlayService.error(
        'This patient curve is unable to be saved until the following issue is resolved: Patients Enrolled must be equal to the sum of Patients Complete and Patients Discontinued.'
      );

      return;
    }

    await this.onSaveAll();
    this.gridApi.stopEditing(false);
  }

  selectCurve(item: PatientCurveModel): void {
    this.loading$.next(true);
    this.currentPatientGroupId = item?.patient_group_id;
    this.currentPatientGroupName$.next(item?.name);

    if (item) {
      if (this.editMode$.getValue()) {
        this.gridApi.stopEditing();
      }

      this.editMode$.next(false);
      this.driverSettingId$.next(item.driver_setting_id);
      this.editCell = false;

      if (item.driver_setting_id === '') {
        this.isFromScratch = true;
        this.patientService
          .get(item.patient_group_id)
          .pipe(take(1))
          .subscribe(() => {
            this.populateGridDataWithTimeline();
            this.isBlended$.next(false);
            this.loading$.next(false);
          });
      } else {
        this.isFromScratch = false;
        this.isBlended$.next(!!item?.is_blended);
        this.patientService
          .get(item.patient_group_id)
          .pipe(take(1))
          .subscribe(() => {
            this.clonePatient();
            this.loading$.next(false);
          });
      }
    } else {
      this.loading$.next(false);
    }
  }

  setEmptyGridData() {
    const { start_date } = this.monthList;
    const { end_date } = this.monthList;

    this.clonedPatient = [];

    if (start_date) {
      let date = Utils.dateParse(`${start_date.slice(0, 8)}01`);
      const month_list: { label: string; value: string }[] = [];

      while (date <= end_date) {
        const value = new Intl.DateTimeFormat('fr-CA', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(date);

        date = Utils.addMonths(date, 1);
        month_list.push({
          label: Utils.dateFormatter(value, { day: undefined, year: '2-digit' }),
          value,
        });
        const dummyPD: listDriverPatientDistributionsQuery = {
          __typename: 'DriverPatientDistribution',
          actual: null,
          distribution_month: value,
          forecast: {
            __typename: 'PatientEnrollmentData',
            id: uuidv4(),
            ...omit(this.zeroValues),
            distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
          },
        };

        if (this.clonedPatient.length < month_list.length) {
          this.clonedPatient.push(dummyPD);
        }
      }
    }

    const newData = Utils.clone(this.clonedPatient);

    newData.forEach((x) => {
      x.forecast.net_patients_enrolled = decimalRoundingToNumber(
        x.forecast.net_patients_enrolled || 0,
        8
      );
      x.forecast.cumulative_enrollment_percentage = decimalRoundingToNumber(
        x.forecast.cumulative_enrollment_percentage || 0,
        8
      );
    });
    this.gridData$.next(newData);
  }

  setGridData(pData: listDriverPatientDistributionsQuery[]) {
    const currentTimeline = this.timelineQuery.getStartEndDateTrial();
    let patientData = [...pData];

    if (currentTimeline.startDate) {
      const timeline = this.distributionTimelineService.getDistributionTimeline(
        {
          endDate: currentTimeline.endDate,
          startDate: currentTimeline.startDate,
        },
        pData
      );

      patientData = patientData.filter(({ distribution_month }) =>
        DateUtils.isInRange(distribution_month, timeline)
      );
    }

    this.clonedPatient = [];
    this.clonedPatient.push(...patientData);

    if (this.clonedPatient.length && this.driverSettingId$.getValue()) {
      this.clonedPatient = this.clonedPatient.map((data) => {
        if (this.shouldPDBeCreatedFromScratch(data)) {
          const newDataId = data.forecast.id ? data.forecast.id : uuidv4();

          void this.patientService.createPatient({
            distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
            distribution_month: data.distribution_month,
            driver_patient_distribution_id: newDataId,
            driver_setting_id: this.driverSettingId$.getValue() || '',
            ...omit(this.zeroValues),
          });
          const newPD: listDriverPatientDistributionsQuery = {
            ...omit(data, ['forecast']),
            forecast: {
              __typename: 'PatientEnrollmentData',
              distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
              id: newDataId,
              ...omit(this.zeroValues),
            },
          };

          return newPD;
        }

        return data;
      });
    }

    const newData = Utils.clone(this.clonedPatient);

    newData.forEach((x) => {
      x.forecast.net_patients_enrolled = decimalRoundingToNumber(
        x.forecast.net_patients_enrolled || 0,
        8
      );
      x.forecast.cumulative_enrollment_percentage = decimalRoundingToNumber(
        x.forecast.cumulative_enrollment_percentage || 0,
        8
      );
    });
    this.gridData$.next(newData);
  }

  shouldPDBeCreatedFromScratch(patient_driver: listDriverPatientDistributionsQuery): boolean {
    const keys = Object.keys(
      omit(patient_driver.forecast, ['id', '__typename', 'distribution_mode'])
    ) as (keyof ForecastAndActualType)[];

    return keys.every((key) => patient_driver.forecast[key] === null);
  }

  private showErrors(): boolean {
    const rowNodes: IRowNode[] = [];
    let isThereAnyInvalidRow = false;

    this.gridApi.forEachNode((node) => {
      const pddq = node.data as listDriverPatientDistributionsQuery;
      const { patients_complete, patients_discontinued, patients_enrolled } = pddq.forecast;

      if (
        Utils.isNumber(patients_enrolled) &&
        Utils.isNumber(patients_discontinued) &&
        Utils.isNumber(patients_complete)
      ) {
        if (node.data.showError) {
          node.data.showError = false;
          rowNodes.push(node);
        }
      } else {
        isThereAnyInvalidRow = true;
        node.data.showError = true;
        rowNodes.push(node);
      }
    });

    this.gridApi.redrawRows({ rowNodes });

    return isThereAnyInvalidRow;
  }

  tabToNextCell(params: TabToNextCellParams): CellPosition | null {
    const previousCell = params.previousCellPosition;
    const lastRowIndex = previousCell.rowIndex;
    const renderedRowCount = params.api?.getModel()?.getRowCount() || 0;
    let nextRowIndex = lastRowIndex;
    let nextCol = previousCell.column;

    if (params.editing) {
      const columnEnum = {
        COMPLETE: 'forecast.patients_complete',
        DISCONTINUED: 'forecast.patients_discontinued',
        ENROLLED: 'forecast.patients_enrolled',
      };

      if (previousCell.column.getColId() === columnEnum.ENROLLED) {
        nextCol = params.api.getColumn(columnEnum.DISCONTINUED) || previousCell.column;
      }

      if (previousCell.column.getColId() === columnEnum.DISCONTINUED) {
        nextCol = params.api.getColumn(columnEnum.COMPLETE) || previousCell.column;
      }

      if (
        previousCell.column.getColId() === columnEnum.COMPLETE &&
        renderedRowCount > lastRowIndex
      ) {
        nextCol = params.api.getColumn(columnEnum.ENROLLED) || previousCell.column;
        nextRowIndex = lastRowIndex + 1;
      }

      if (nextRowIndex >= renderedRowCount) {
        nextRowIndex = renderedRowCount - 1;
      }

      return {
        column: nextCol,
        rowIndex: nextRowIndex,
        rowPinned: previousCell.rowPinned,
      };
    } else {
      if (params.nextCellPosition?.column.getColId().includes('spacer')) {
        const nextColIndex = params.api
          .getAllDisplayedColumns()
          .findIndex((col) => col.getColId() === params.nextCellPosition?.column.getColId());
        const nextColumn = params.api.getAllDisplayedColumns()[nextColIndex + 1];

        return {
          column: nextColumn,
          rowIndex: params.previousCellPosition.rowIndex,
          rowPinned: params.previousCellPosition.rowPinned,
        };
      } else {
        return params.nextCellPosition;
      }
    }
  }

  private updatePatientCurveList(items: PatientCurveModel[]): void {
    this.patientCurveGroups = items;
    this.patientCurveListOptions = items.map((item: PatientCurveModel) => ({
      isDeletable: item.driver_setting_id.length > 1,
      isEditable: item.is_blended,
      name: item.name,
      showLine: item.showLine,
      value: item.name,
    }));
  }

  valueFormatter(val: ValueFormatterParams) {
    const newValue = val.value === '0' ? 0 : val.value;

    return newValue ? decimalRoundingToString(newValue) : Utils.zeroHyphen;
  }
}
